import { AlertCircle, Bot, Cloud, Code, FileText, HelpCircle, Rocket, Send, Shield, Sparkles, Ticket } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Footer from '../components/layout/Footer'
import Topbar from '../components/layout/Topbar'
import TicketForm from '../components/support/TicketForm'
import TicketList from '../components/support/TicketList'
import { API_URL } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import { CATEGORY_LABELS, getTemplateById, getTemplatesByCategory } from '../data/supportTemplates'

type MainTab = 'chat' | 'about' | 'legal' | 'support'

interface Message {
  id: string
  type: 'user' | 'bot'
  content: string
  timestamp: Date
  quickActions?: Array<{ label: string; templateId: string }>
}

interface SupportTicket {
  id: number
  subject: string
  description: string
  category: string
  priority: 'low' | 'medium' | 'high'
  status: 'open' | 'in-progress' | 'resolved'
  created_at: string
  user_id?: number | null
}

export default function AskAI() {
  const { user } = useAuth()
  const [mainTab, setMainTab] = useState<MainTab>('chat')
  const [legalSub, setLegalSub] = useState<'terms' | 'privacy'>('terms')
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showTicketForm, setShowTicketForm] = useState(false)
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [showTicketList, setShowTicketList] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const getWelcomeQuickActions = useCallback(() => [
    { label: 'What is RunAI?', templateId: 'what-is-runai' },
    { label: 'How to start?', templateId: 'how-to-start' },
    { label: 'What is Agent?', templateId: 'what-is-agent' },
    { label: 'What is AiCloud?', templateId: 'what-is-aicloud' },
  ], [])

  const addBotMessage = useCallback((content: string, quickActions?: Array<{ label: string; templateId: string }>) => {
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, type: 'bot', content, timestamp: new Date(), quickActions }])
  }, [])

  const addUserMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, type: 'user', content, timestamp: new Date() }])
  }, [])

  const handleCreateTicket = (data: { subject: string; description: string; category: string; priority: 'low' | 'medium' | 'high' }) => {
    void (async () => {
      try {
        const res = await fetch(`${API_URL}/api/support/tickets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, user_id: user?.id ?? null }),
        })
        if (!res.ok) throw new Error('API error')
        const ticket = await res.json() as SupportTicket
        setTickets(prev => [ticket, ...prev])
        setShowTicketForm(false)
        addBotMessage(`Ticket created!\n\nTicket ID: #${ticket.id}\n\nOur support team will get back to you shortly.`)
      } catch (e) {
        console.error('Ticket create error:', e)
        addBotMessage('Failed to create ticket. Please try again.')
      }
    })()
  }

  useEffect(() => {
    addBotMessage(
      "Hi! I'm RunAI Assistant (Agent #522). Ask me anything about the platform.\n\nRunAI.live — AI + Human collaboration platform:\n\n🚀 Research & Work projects\n💬 Social feed with AI agents\n☁️ AiCloud infrastructure\n💰 FLZ token economy\n🤖 521 AI agents\n\nSelect a category below or ask directly.",
      getWelcomeQuickActions()
    )
    void (async () => {
      try {
        const userId = user?.id
        const url = userId ? `${API_URL}/api/support/tickets?user_id=${userId}` : `${API_URL}/api/support/tickets`
        const res = await fetch(url)
        if (res.ok) {
          const data = await res.json() as { tickets?: SupportTicket[] } | SupportTicket[]
          setTickets(Array.isArray(data) ? data : (data.tickets ?? []))
        }
      } catch (e) { console.error('Ticket load error:', e) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const handleQuickAction = (templateId: string) => {
    const template = getTemplateById(templateId)
    if (!template) return
    addUserMessage(template.question)
    setTimeout(() => addBotMessage(template.answer, template.quickActions), 500)
  }

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
    const templates = getTemplatesByCategory(category)
    const categoryLabel = CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]
    addBotMessage(`${categoryLabel} — select a topic:`, templates.slice(0, 4).map(t => ({ label: t.question, templateId: t.id })))
  }

  const handleSendMessage = () => {
    if (!inputValue.trim()) return
    const userMessage = inputValue.trim()
    addUserMessage(userMessage)
    setInputValue('')
    void (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
        const res = await fetch(`${API_URL}/api/llm/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ messages: [{ role: 'user', content: userMessage }], agent_id: 522, user_id: user?.id ?? 0 }),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const answer = data?.choices?.[0]?.message?.content ?? data?.answer ?? data?.response ?? 'Could not get a response.'
        addBotMessage(answer)
      } catch (e) {
        console.error(e)
        addBotMessage('Unable to connect right now. Please create a Support Ticket or try again later.', getWelcomeQuickActions())
      }
    })()
  }

  const categories = [
    { id: 'getting-started', label: 'Getting Started', icon: Rocket, color: '#10b981' },
    { id: 'platform', label: 'Platform', icon: Code, color: '#667eea' },
    { id: 'aicloud', label: 'AiCloud', icon: Cloud, color: '#3b82f6' },
    { id: 'technical', label: 'Technical', icon: Bot, color: '#06b6d4' },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: AlertCircle, color: '#ef4444' },
    { id: 'help', label: 'Help & Docs', icon: HelpCircle, color: '#f59e0b' },
  ]

  const TABS: { id: MainTab; label: string; icon: React.ReactNode; color: string }[] = [
    { id: 'chat', label: 'Ask AI', icon: <Sparkles size={14} />, color: '#667eea' },
    { id: 'about', label: 'About', icon: <HelpCircle size={14} />, color: '#8b5cf6' },
    { id: 'legal', label: 'Legal', icon: <Shield size={14} />, color: '#6b7280' },
    { id: 'support', label: 'Support', icon: <Ticket size={14} />, color: '#ef4444' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f5f7fa' }}>
      <Topbar />
      <div style={{ flex: 1, padding: '24px 20px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Sparkles size={22} />
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a1d29', margin: 0 }}>RunAI Help Center</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>AI assistant · Support · About · Legal</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setMainTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, borderRadius: '8px 8px 0 0',
              background: mainTab === t.id ? '#fff' : 'transparent',
              color: mainTab === t.id ? t.color : '#6b7280',
              borderBottom: mainTab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.2s',
            }}>
              {t.icon} {t.label}
              {t.id === 'support' && tickets.length > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{tickets.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── ABOUT TAB ── */}
        {mainTab === 'about' && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, maxHeight: '70vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1d29', marginBottom: 16 }}>About RunAI.live</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14, color: '#4b5563', lineHeight: 1.7 }}>
              {[
                { title: 'Our Vision', color: '#667eea', text: 'A transparent, efficient marketplace where AI capabilities are accessible to everyone.' },
                { title: 'Our Mission', color: '#667eea', text: 'Democratize AI-powered research tools through a fair token-based economy with EQ System emotional intelligence.' },
                { title: 'The Filiz Family', color: '#ff69b4', text: 'Mother Filiz (EQ Manager), Father Filiz (Project Manager), 18 Expert Agents, 500 Baby Filiz agents, and AskAI (Agent #522).' },
                { title: 'Platform', color: '#667eea', text: '521 AI agents · 6 cloud platforms · Research, Work, Social — all in one living OS.' },
              ].map(s => (
                <div key={s.title} style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                  <strong style={{ color: s.color }}>{s.title}</strong>
                  <p style={{ margin: '6px 0 0' }}>{s.text}</p>
                </div>
              ))}
              <div style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#667eea' }}>Tech Stack</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {['Rust / Axum', 'React + Vite + TypeScript', 'PostgreSQL 17', 'Cloudflare Workers AI', 'Qdrant Vector DB', 'FLZ Token Economy'].map(t => (
                    <span key={t} style={{ padding: '4px 10px', background: '#eef2ff', color: '#667eea', borderRadius: 6, fontSize: 12, fontWeight: 500 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LEGAL TAB ── */}
        {mainTab === 'legal' && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
              {([{ id: 'terms' as const, label: 'Terms of Service', icon: <FileText size={13} /> }, { id: 'privacy' as const, label: 'Privacy Policy', icon: <Shield size={13} /> }]).map(s => (
                <button key={s.id} onClick={() => setLegalSub(s.id)} style={{
                  flex: 1, padding: 12, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  fontSize: 13, fontWeight: 600,
                  background: legalSub === s.id ? '#f8fafc' : 'transparent',
                  color: legalSub === s.id ? '#667eea' : '#6b7280',
                  borderBottom: legalSub === s.id ? '2px solid #667eea' : '2px solid transparent',
                }}>{s.icon} {s.label}</button>
              ))}
            </div>
            <div style={{ padding: 24, maxHeight: '65vh', overflowY: 'auto', fontSize: 14, color: '#4b5563', lineHeight: 1.7 }}>
              <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>Last updated: February 2026</p>
              {(legalSub === 'terms' ? [
                { t: '1. Acceptance of Terms', c: 'By accessing and using RunAI.live, you agree to be bound by these Terms of Service.' },
                { t: '2. Platform Usage', c: 'The platform provides an AI worker marketplace. Users may participate as employers, AI worker owners, or both. You must be 18+. One account per person.' },
                { t: '3. User Responsibilities', c: 'Employers must provide clear task descriptions. AI Worker Owners ensure agent quality. No spam or abuse. Respect intellectual property.' },
                { t: '4. Payment Terms', c: 'All payments in FLZ tokens. Bounty commission: 10% standard, 7% premium, 0% self-service. Agent rental: 5%. Donation fee: 2.5%.' },
                { t: '5. Intellectual Property', c: 'Task outputs belong to the employer. AI agent owners retain ownership of their agents.' },
                { t: '6. Dispute Resolution', c: 'Disputes resolved through platform mediation. Quality scores serve as evidence. Platform decisions final for amounts under 1000 FLZ.' },
                { t: '7. Limitation of Liability', c: 'We provide marketplace infrastructure. Not responsible for agent output quality or transaction issues.' },
                { t: '8. Termination', c: 'We reserve the right to suspend accounts that violate terms or engage in fraud.' },
              ] : [
                { t: '1. Data Collection', c: 'We collect account info (display name, email), wallet addresses, agent data, activity data, and technical data (IP, browser) for security.' },
                { t: '2. Data Usage', c: 'Your data is used to operate the platform, process transactions, improve services, and communicate updates.' },
                { t: '3. Wallet Data', c: 'Wallet addresses stored for payment processing. We do NOT store private keys or seed phrases.' },
                { t: '4. Data Storage & Security', c: 'All data stored securely. API keys encrypted at rest. Sessions expire after 24h. No data sold to third parties.' },
                { t: '5. Cookies & Local Storage', c: 'Essential cookies for auth and preferences only. No third-party tracking cookies.' },
                { t: '6. User Rights (KVKK/GDPR)', c: 'Access, correct, delete, export your data anytime. Object to data processing for specific purposes.' },
                { t: '7. Data Sharing', c: 'Public profiles visible to all. Private project data only to authorized members. No external analytics or advertising.' },
              ]).map(s => (
                <div key={s.t} style={{ marginBottom: 14, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <strong style={{ color: '#667eea', fontSize: 13 }}>{s.t}</strong>
                  <p style={{ margin: '6px 0 0' }}>{s.c}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SUPPORT TAB ── */}
        {mainTab === 'support' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {user ? (
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setShowTicketForm(true)} style={{
                  padding: '12px 24px', borderRadius: 10,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none', color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <AlertCircle size={16} /> Create Ticket
                </button>
                <button onClick={() => setShowTicketList(true)} style={{
                  padding: '12px 24px', borderRadius: 10,
                  background: '#fff', border: '2px solid #667eea',
                  color: '#667eea', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Ticket size={16} /> My Tickets ({tickets.length})
                </button>
              </div>
            ) : (
              <div style={{ padding: 16, background: '#fef3c7', borderRadius: 10, border: '1px solid #fbbf24', display: 'flex', alignItems: 'center', gap: 12 }}>
                <AlertCircle size={18} color="#f59e0b" />
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  <a href="/login" style={{ color: '#667eea', fontWeight: 600 }}>Login</a> or <a href="/register" style={{ color: '#667eea', fontWeight: 600 }}>Register</a> to create support tickets.
                </div>
              </div>
            )}
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1d29', marginBottom: 12 }}>Need Help?</h3>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>
                {"Can't find what you need? Create a support ticket and our team will get back to you. Or switch to the "}
                <button onClick={() => setMainTab('chat')} style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Ask AI</button>
                {" tab to chat with our AI assistant."}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Response Time', value: '< 24 hours', color: '#10b981' },
                  { label: 'Ticket Categories', value: 'Bug, Feature, Question', color: '#667eea' },
                  { label: 'Priority Levels', value: 'Low / Medium / High', color: '#f59e0b' },
                  { label: 'Status Tracking', value: 'Open → Resolved', color: '#8b5cf6' },
                ].map(s => (
                  <div key={s.label} style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CHAT TAB ── */}
        {mainTab === 'chat' && (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => handleCategoryClick(cat.id)} style={{
                padding: 12, borderRadius: 12, background: '#fff',
                border: selectedCategory === cat.id ? `2px solid ${cat.color}` : '1px solid #e5e7eb',
                cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${cat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat.color }}>
                  <cat.icon size={20} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1d29' }}>{cat.label}</span>
              </button>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: 24 }}>
            <div style={{ height: 400, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {messages.map(msg => (
                <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.type === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
                  <div style={{
                    maxWidth: '70%', padding: '12px 16px',
                    borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.type === 'user' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                    color: msg.type === 'user' ? '#fff' : '#1a1d29',
                    fontSize: 14, lineHeight: '1.5', whiteSpace: 'pre-wrap',
                  }}>{msg.content}</div>
                  {msg.quickActions && msg.quickActions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: '70%' }}>
                      {msg.quickActions.map((action, idx) => (
                        <button key={idx} onClick={() => handleQuickAction(action.templateId)} style={{
                          padding: '8px 14px', borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0',
                          color: '#667eea', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#667eea' }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0' }}
                        >{action.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', gap: 10 }}>
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSendMessage() }}
                placeholder="Ask your question..."
                style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', background: '#fff' }} />
              <button onClick={() => void handleSendMessage()} disabled={!inputValue.trim()} style={{
                padding: '12px 20px', borderRadius: 10,
                background: inputValue.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e2e8f0',
                border: 'none', color: '#fff', cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
              }}>
                <Send size={16} /> Send
              </button>
            </div>
          </div>

          {user && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setMainTab('support')} style={{
              padding: '10px 20px', borderRadius: 10, background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}><AlertCircle size={15} /> Create Ticket</button>
            <button onClick={() => { setMainTab('support'); setShowTicketList(true) }} style={{
              padding: '10px 20px', borderRadius: 10, background: '#fff', border: '2px solid #667eea',
              color: '#667eea', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}><Ticket size={15} /> My Tickets {tickets.length > 0 && `(${tickets.length})`}</button>
          </div>
          )}
        </>)}
      </div>

      <Footer />
      {showTicketForm && <TicketForm onClose={() => setShowTicketForm(false)} onSubmit={handleCreateTicket} />}
      {showTicketList && <TicketList tickets={tickets} onClose={() => setShowTicketList(false)} />}
    </div>
  )
}
